<i18n>
[
    "product__inBox--label",
    "product__addToBox--label",
    "global__cancel",
    "global__retail",
    "product__reviewItem--cta",
]
</i18n>

<template>
    <div
        :class="{
            'u-spacer--2': useBottomMargin,
        }"
        class="c-catalog-tile"
    >
        <template v-if="hasProduct">
            <div class="c-catalog-tile__image-and-info">
                <div
                    class="c-catalog-tile__image-container"
                    :class="{
                        'c-catalog-tile__image-container--carousel': displayAsCarousel && isMounted,
                    }"
                >
                    <div
                        class="c-catalog-tile__fade"
                        :class="{
                            'c-catalog-tile__fade--in-view': hasEnteredViewport
                        }"
                    >
                        <div
                            v-if="hasEnteredViewport && isMounted"
                            class="c-catalog-tile__image-container-inner"
                        >
                            <CatalogImageViewer
                                v-if="displayAsCarousel"
                                :analyticsType="analyticsType"
                                :productImages="productImages"
                                :productLink="productRoute"
                                :productName="product?.displayName"
                                :productAnalyticsClick="handleProductClick"
                                :imagePreset="imagePreset"
                                :catalogResponseId="catalogResponseId"
                            />
                            <BaseLink
                                v-else
                                :to="productRoute"
                                class="u-display--block c-catalog-tile__image-container-inner__link"
                                :tabindex="tabIndex"
                                @click="handleProductClick"
                            >
                                <ProductImage
                                    :src="productImage"
                                    :title="product?.displayName"
                                    :choiceId="product?.choiceId"
                                    :imagePreset="imagePreset"
                                    :isLazyImage="true"
                                    :shouldDisplayImage="true"
                                    :fadeInAfterLoaded="true"
                                    :allowFocusOutline="true"
                                    class="c-catalog-tile__image"
                                />
                            </BaseLink>
                            <ClosetBadge
                                v-if="showClosetButton"
                                :loggedIn="loggedIn"
                                :initialIsActive="productIsInCloset"
                                :isSmall="smallClosetButton || mediaQueries.isSmallish"
                                :showInProductTour="showInProductTour"
                                :choiceId="product?.choiceId"
                                :closetListId="closetListId"
                                :productAnalyticsData="productAnalyticsData"
                                :product="product"
                                :toastOptions="{ images: toastImages, description: toastDescription }"
                                :popoverOffsets="popoverOffsets"
                                :catalogResponseId="catalogResponseId"
                                :isTabbable="isTabbable"
                                class="c-catalog-tile__closet-badge"
                                @click="closetBadgeClicked"
                            />
                            <CatalogShopTheLook
                                v-if="showShopTheLookBanner"
                                class="
                                    c-catalog-tile__shop-the-look
                                    o-flex-vertical-center
                                    o-flex-horizontal-center
                                    o-text--label
                                "
                            />
                        </div>
                    </div>
                </div>
                <BaseLink
                    v-if="showReviewButton"
                    :to="productReviewRoute"
                    :isButton="true"
                    :isBorderless="true"
                    :tabindex="tabIndex"
                    colorVariant="light"
                    class="c-catalog-tile__review-link"
                >
                    {{ t('product__reviewItem--cta') }}
                </BaseLink>
                <CatalogTileDetails
                    :product="product"
                    :productRoute="productRoute"
                    :hasEnteredViewport="hasEnteredViewport"
                    :hideInfo="hideInfo"
                    :productTrayId="productTrayId"
                    :catalogResponseId="catalogResponseId"
                    :gridLayout="gridLayout"
                    :showShopTheLookBanner="showShopTheLookBanner"
                    :productListName="productListName"
                    :productListId="productListId"
                    :isPrePurchase="isPrePurchase"
                    :lightInfoContainer="lightInfoContainer"
                    @click="handleProductClick"
                >
                    <Transition name="slide-sku" mode="in-out">
                        <div v-if="showSkuSelector" class="c-catalog-tile__sku-container">
                            <div
                                class="c-catalog-tile__sku-info"
                                @click="skuSelectorStatus"
                            >
                                <span>{{ skuMessage }}</span>
                                <AppIcon
                                    icon="global--close"
                                    size="16px"
                                    color="primary"
                                    class="c-catalog-tile__sku-container-close"
                                />
                            </div>
                            <SkuSelector
                                class="c-catalog-tile__sku-selector"
                                :skuSelectorId="`closet-sku-selector-${product?.choiceId}`"
                                :skuSelectorName="`closet-sku-selector-${product?.choiceId}`"
                                :sizeGroups="sizeGroups"
                                :selectedColorCode="product?.colorCode"
                                :initialSizeType="sizeType"
                                :initialSize="size"
                                :initialSku="selectedSku"
                                :skuErrorMessage="skuErrorMessage"
                                :productDetails="productDetails"
                                @update-sku="chooseSize"
                            />
                        </div>
                    </Transition>
                </CatalogTileDetails>
            </div>
            <div
                class="c-catalog-tile__fade"
                :class="{
                    'c-catalog-tile__fade--in-view': hasEnteredViewport,
                }"
            >
                <template v-if="showAddToBox">
                    <button
                        :class="{
                            'c-catalog-tile__box-button': true,
                            'c-catalog-tile__box-button--desktop': true,
                            'c-catalog-tile__box-button--active': productIsInBox,
                            'c-catalog-tile__box-button--inactive': showAddToBox && !productIsInBox,
                        }"
                        :tabindex="tabIndex"
                        @click="skuSelectorStatus"
                    >
                        <SuccessCheck ref="successCheckRefDesktop" :light="true">
                            <template #action>
                                <AppIcon
                                    v-if="productIsInBox"
                                    icon="global--checkmark"
                                    size="12px"
                                    color="white"
                                    transitionDuration="0"
                                    class="c-success-check__icon"
                                />
                                <span class="o-text--caption">{{ boxButtonText }}</span>
                            </template>
                        </SuccessCheck>
                    </button>
                    <UrbnModalLauncher
                        :disabled="productIsInBox"
                        :modalOptions="{
                            ...catalogSkuSelectorModal,
                            props: {
                                product,
                                productAnalyticsData,
                                catalogResponseId,
                            },
                        }"
                        :class="{
                            'c-catalog-tile__box-button': true,
                            'c-catalog-tile__box-button--mobile': true,
                            'c-catalog-tile__box-button--active': productIsInBox,
                            'c-catalog-tile__box-button--inactive': showAddToBox && !productIsInBox,
                        }"
                        :tabindex="tabIndex"
                        variant="none"
                        type="button"
                    >
                        <SuccessCheck ref="successCheckRefMobile" :light="true">
                            <template #action>
                                <AppIcon
                                    v-if="productIsInBox"
                                    icon="global--checkmark"
                                    size="12px"
                                    color="white"
                                    transitionDuration="0"
                                    class="c-success-check__icon"
                                />
                                <span class="o-text--caption">{{ boxButtonText }}</span>
                            </template>
                        </SuccessCheck>
                    </UrbnModalLauncher>
                </template>
            </div>

            <div
                class="c-catalog-tile__fade"
                :class="{
                    'c-catalog-tile__fade--in-view': hasEnteredViewport,
                }"
            >
                <CatalogAddToListButton
                    v-if="showAddToList"
                    :choiceId="product?.choiceId"
                    :closetListId="productListId"
                    :product="product"
                    :productAnalyticsData="productAnalyticsData"
                    :catalogResponseId="catalogResponseId"
                    :isTabbable="isTabbable"
                />
            </div>
        </template>
        <EmptyCatalogTile
            v-else
            :illustration="emptyTile?.illustration"
            :text="emptyTile?.text"
        />
    </div>
</template>

<script setup>
import { inject, ref, computed, watch, onMounted } from 'vue';

import { useI18n } from 'vue-i18n';
import { useState, useGetters, useActions, useMutations } from '~coreModules/core/js/composables/vuexHelpers';
import { useMediaQueries } from '~coreModules/core/js/composables/useMediaQueries';
import { useR15Svc } from '~coreModules/core/js/composables/useR15Svc';
import { useLogger } from '~coreModules/core/js/composables/useLogger';

import { URBN_BRANDS } from '~coreModules/core/js/constants';
import { GLOBAL_EVENT } from '~coreModules/core/js/store';
import { PRODUCT_CLICK } from '~coreModules/core/js/global-event-constants';
import { SET_PRODUCT_ATTRIBUTION } from '~modules/core/js/store';
import { ADD_BOX_ITEM, BOX_MODULE_NAME } from '~modules/box/js/box-store';
import { SITE_SETTINGS_MODULE_NAME } from '~modules/site-settings/js/site-settings-store';
import { injectedPrePurchase } from '~modules/core/js/provider-symbol-constants';

import { CATALOG_IMAGE_VIEWER_MAX_NUM_IMAGES } from '~coreModules/catalog/js/catalog-constants';
import {
    CATALOG_GRID_LAYOUTS,
    CATALOG_SHOP_THE_LOOK_TRAY_LIST_NAME,
    CATALOG_FACE_OUT_TYPES,
} from '~modules/catalog/js/catalog-constants';
import { rentalRouteNames } from '~modules/core/js/router-constants';
import { MODALS } from '~modules/modals/js/modal-constants';
import { PRODUCT_AFFILIATION, PRODUCT_BUSINESSES } from '~modules/analytics/js/analytics-constants';
import { REVIEW_SCHEMES } from '~routes/review/js/review-constants';

import { getProductDetails } from '~routes/product/js/product-api';
import { getPlusSizeFaceOut } from '~routes/product/js/product-utils';
import { getFilterStringFromObject } from '~coreModules/catalog/js/catalog-utils';

import CatalogImageViewer from '~coreModules/catalog/components/CatalogImageViewer.vue';
import UrbnModalLauncher from '~coreModules/modals/components/UrbnModalLauncher.vue';
import ProductImage from '~coreModules/core/components/ui/ProductImage.vue';
import SuccessCheck from '~coreModules/core/components/ui/SuccessCheck.vue';

import CatalogAddToListButton from '~modules/catalog/components/CatalogAddToListButton.vue';
import CatalogTileDetails from '~modules/catalog/components/CatalogTileDetails.vue';
import CatalogShopTheLook from '~modules/catalog/components/CatalogShopTheLook.vue';
import ClosetBadge from '~routes/closet/components/ClosetBadge.vue';
import SkuSelector from '~modules/core/components/ui/SkuSelector.vue';
import EmptyCatalogTile from '~coreModules/catalog/components/EmptyCatalogTile.vue';

const isInjectedPrePurchase = inject(injectedPrePurchase, false);

// NU_TODO: https://urbnit.atlassian.net/browse/TYP-26682
// Remove below when we update to vue-eslint-parser v9.0.0 or later
/* eslint-disable vue/valid-define-props */
const props = defineProps({
    analyticsType: {
        type: String,
        default: null,
    },
    product: {
        type: Object,
        default: () => ({}),
    },
    displayAsCarousel: {
        type: Boolean,
        default: false,
    },
    imagePreset: {
        type: String,
        required: true,
    },
    showAddToBox: {
        type: Boolean,
        default: false,
    },
    showAddToList: {
        type: Boolean,
        default: false,
    },
    showShopTheLookBanner: {
        type: Boolean,
        default: false,
    },
    showReviewButton: {
        type: Boolean,
        default: false,
    },
    showClosetButton: {
        type: Boolean,
        default: true,
    },
    gridLayout: {
        type: String,
        default: CATALOG_GRID_LAYOUTS.LARGE,
    },
    hideInfo: {
        type: Boolean,
        default: false,
    },
    smallClosetButton: {
        type: Boolean,
        default: false,
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
    productTrayId: {
        type: String,
        default: null,
    },
    showInProductTour: {
        type: Boolean,
        default: false,
    },
    useBottomMargin: {
        type: Boolean,
        default: true,
    },
    catalogResponseId: {
        type: String,
        default: null,
    },
    lightInfoContainer: {
        type: Boolean,
        default: true,
    },
    index: {
        type: Number,
        default: null,
    },
    productListId: {
        type: String,
        default: '',
    },
    productListName: {
        type: String,
        default: '',
    },
    closetListId: {
        type: String,
        default: '',
    },
    isPropPrePurchase: {
        type: Boolean,
        default: false,
    },
    selectedCuration: {
        type: String,
        default: '',
    },
    faceOutImage: {
        type: String,
        default: null,
    },
    queryFilters: {
        type: Object,
        default: () => ({}),
    },
    emptyTile: {
        type: Object,
        default: () => ({}),
    },
    isTabbable: {
        type: Boolean,
        default: true,
    },
});

const { t } = useI18n();
const mediaQueries = useMediaQueries();
const r15Svc = useR15Svc();
const logger = useLogger();

const emit = defineEmits(['toggle-save-product-clicked']);

const closetIsLoading = ref(false);
const isMounted = ref(false);
const showSkuSelector = ref(false);
const productDetails = ref({});
const selectedSku = ref('');
const sizeType = ref('');
const size = ref('');
const skuMessage = ref('Select a Size');
const skuErrorMessage = ref('');
const catalogSkuSelectorModal = MODALS.catalogSkuSelector;
const productIsInBox = ref(!!props.product?.inBox);
const productIsInCloset = ref(!!props.product?.inCloset);
const successCheckRefDesktop = ref(null);
const successCheckRefMobile = ref(null);

const { loggedIn } = useGetters(['loggedIn']);
const { inventoryDCPoolOverride } = useState(SITE_SETTINGS_MODULE_NAME, {
    inventoryDCPoolOverride: state => state.inventoryDCPoolOverride,
});

const { trackGlobalEvent } = useActions({
    trackGlobalEvent: GLOBAL_EVENT,
});

const { setProductAttribution } = useMutations({
    setProductAttribution: SET_PRODUCT_ATTRIBUTION,
});

const { addBoxItem } = useActions(BOX_MODULE_NAME, {
    addBoxItem: ADD_BOX_ITEM,
});

const hasProduct = computed(() => props.product && !!Object.keys(props.product).length);
const skuUrl = computed(() => `${props.product?.productSlug}?color=${props.product?.colorCode}`);
const isPrePurchase = computed(() => props.isPropPrePurchase || isInjectedPrePurchase.value);
const tabIndex = computed(() => (props.isTabbable ? 0 : -1));

const productReviewRoute = computed(() => ({
    name: rentalRouteNames.feedback.product,
    params: {
        scheme: REVIEW_SCHEMES.PRODUCT,
        sku: props.product?.skuId,
        colorCode: props.product?.colorCode,
    },
}));

const productImages = computed(() => {
    let images = props.product?.images ?? [];
    if (props.faceOutImage === CATALOG_FACE_OUT_TYPES.PLUS) {
        images = getPlusSizeFaceOut(images);
    }
    if (images.length > CATALOG_IMAGE_VIEWER_MAX_NUM_IMAGES) {
        return images.slice(0, CATALOG_IMAGE_VIEWER_MAX_NUM_IMAGES);
    }
    return images;
});

const productImage = computed(() => (productImages.value.length ? productImages.value[0] : ''));

const boxButtonText = computed(() => {
    if (productIsInBox.value) {
        return t('product__inBox--label');
    }
    if (props.showAddToBox && !productIsInBox.value && !showSkuSelector.value) {
        return t('product__addToBox--label');
    }
    return t('global__cancel');
});

const sizeGroups = computed(() => {
    const selectedChoice = productDetails.value?.choices
        .find(choice => choice.color.code === props.product?.colorCode);
    return selectedChoice?.sizeGroups ?? [];
});

// eslint-disable-next-line no-use-before-define
const brandName = computed(() => filterVendorBrands(props.product?.vendorBrands));

const productRoute = computed(() => ({
    name: rentalRouteNames.product,
    params: { slug: props.product?.productSlug },
    query: {
        color: props.product?.colorCode,
        ...(isPrePurchase.value && { view: 'buy' }),
    },
}));

const productAnalyticsData = computed(() => {
    const trayListName = props.showShopTheLookBanner ?
        CATALOG_SHOP_THE_LOOK_TRAY_LIST_NAME : props.productListName;

    const business = isPrePurchase.value ?
        PRODUCT_BUSINESSES.PREPURCHASE : PRODUCT_BUSINESSES.RENT;

    return {
        productList: {
            responseId: props.catalogResponseId,
            filterString: getFilterStringFromObject(props.queryFilters),
        },
        product: {
            productListId: props.productListId,
            productListName: trayListName,
            productPrice: props.product?.msrpValue,
            productId: props.product?.choiceId,
            productName: props.product?.displayName,
            productAffiliation: PRODUCT_AFFILIATION,
            productBrand: brandName.value,
        },
        business,
        curation: props.selectedCuration,
        faceOutImage: productImage.value,
    };
});

const toastDescription = computed(() => `${t('closet__savedToCloset--confirmation')} '${props.product?.displayName}'`);

const toastImages = computed(() => [].concat(productImage.value).filter(val => !!val));

const popoverOffsets = computed(() => (mediaQueries.isSmallish ? { x: 8, y: -4 } : { x: 16, y: -12 }));

function closetBadgeClicked(isInCloset) {
    productIsInCloset.value = isInCloset;

    emit('toggle-save-product-clicked', {
        index: props.index,
        choiceId: props.product?.choiceId,
    });
}

function fetchProductDetails() {
    return getProductDetails({
        r15Svc,
        productSlug: skuUrl.value,
        dcOverride: inventoryDCPoolOverride.value,
    }).then((res) => {
        productDetails.value = res;
    }).then(() => {
        showSkuSelector.value = true;
    }).catch((error) => {
        logger.debugError(`Failed to get product details for ${skuUrl.value}: `, error);
    });
}

function skuSelectorStatus() {
    if (props.showAddToBox && !productIsInBox.value && showSkuSelector.value) {
        showSkuSelector.value = false;
        skuErrorMessage.value = '';
        return false;
    }
    if (props.showAddToBox && !productIsInBox.value && !showSkuSelector.value) {
        fetchProductDetails();
        return false;
    }
    return false;
}

function chooseSize(sku) {
    selectedSku.value = sku?.skuId;
    if (!selectedSku.value) return false;

    return addBoxItem({
        sku: selectedSku.value,
        productAnalyticsData: productAnalyticsData.value,
        catalogResponseId: props.catalogResponseId,
    })
        .then(() => {
            showSkuSelector.value = false;
        })
        .catch((error) => {
            productIsInBox.value = false;
            skuErrorMessage.value = error?.data?.message || '';
        });
}

function filterVendorBrands(vendorBrands) {
    if (!vendorBrands.length) return '';
    if (vendorBrands.length > 1) {
        return vendorBrands.filter(brand => !URBN_BRANDS.includes(brand))[0];
    }

    return vendorBrands[0];
}

function handleProductClick() {
    setProductAttribution({
        productSlug: props.product?.productSlug,
        catalogResponseId: props.catalogResponseId,
        curationName: props.selectedCuration,
    });

    trackGlobalEvent({
        type: PRODUCT_CLICK,
        data: productAnalyticsData.value,
    });
}

watch(() => props.product?.inCloset, (isInCloset) => {
    productIsInCloset.value = isInCloset;
    closetIsLoading.value = false;
}, { deep: true });

watch(() => props.product?.inBox, (isInBox) => {
    productIsInBox.value = isInBox;
    successCheckRefDesktop.value?.changeSuccessMessageStatus?.();
    successCheckRefMobile.value?.changeSuccessMessageStatus?.();
});

onMounted(() => {
    isMounted.value = true;
});
</script>

<style lang='scss'>
    .c-catalog-tile {
        width: 100%;
        height: 100%;
        background-color: $placeholder-background;
        position: relative;

        @include css-has-supported {
            &:has(.c-carousel__container.focus-visible:focus[data-focus-visible-added]),
            &:has(.c-catalog-tile__image-container-inner__link.focus-visible:focus[data-focus-visible-added]) {
                outline: $focus-outline;
            }
        }

        &__fade {
            opacity: 0;
            transition: opacity .5s ease;

            &--in-view {
                opacity: 1;
            }
        }

        &__image-and-info {
            position: relative;
        }

        &__image-container {
            position: relative;

            &::before {
                position: relative;
                display: block;
                content: "";
                width: 100%;
                padding-top: 150%;
                background-color: $placeholder-background;
            }

            &--carousel::before {
                padding-top: 125%;
            }

            @include breakpoint(medium) {
                &--carousel::before {
                    padding-top: 130%;
                }
            }

            @include breakpoint(large) {
                &--carousel::before {
                    padding-top: 150%;
                }
            }
        }

        &__image-container-inner {
            position: absolute;
            top: 0;
            width: 100%;

            &__link.focus-visible:focus[data-focus-visible-added] {
                outline: 0;
            }
        }

        &__image {
            width: 100%
        }

        &__sku-container-close {
            position: absolute;
            right: $nu-spacer-2;
            top: 0;
            bottom: 0;
            margin: auto;
            cursor: pointer;
        }

        &__closet-badge {
            position: absolute;
            top: 0;
            right: 0;
        }

        &__shop-the-look {
            position: absolute;
            bottom: $nu-spacer-0pt5;
            width: 100%;
            height: 32px;
            background-color: $nu-gray--light;
            pointer-events: none;
            opacity: .8;
            color: $nu-gray;
        }

        &__sku-container {
            width: 100%;
            background-color: $nu-white;
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
        }

        &__sku-info {
            padding: $nu-spacer-1pt5;
            cursor: pointer;
            border-bottom: 1px solid $nu-gray--light;
            text-align: center;
            position: relative;
        }

        &__sku-selector {
            background: $nu-gray--light;
            padding: $nu-spacer-2;
        }

        &__review-link {
            width: 100%;
            background-color: $nu-white;
            outline-offset: -4px;
        }

        &__box-button {
            position: relative;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 40px;
            cursor: pointer;
            outline: none;
            border: none;
            border-top: 1px solid $nu-gray--light;

            &--active {
                color: $nu-white;
                background-color: $nu-primary;
            }

            &--inactive {
                color: $nu-primary;
                background-color: $nu-white;
            }

            &--desktop {
                display: none;
                @include breakpoint(medium) {
                    display: block;
                }
            }

            &--mobile {
                display: flex;
                @include breakpoint(medium) {
                    display: none;
                }
            }
        }
    }

    .slide-sku {
        &-enter-from,
        &-leave-to {
            max-height: 0px
        }

        &-enter-to,
        &-leave-from {
            max-height: 200px
        }

        &-enter-active,
        &-leave-active {
            overflow: hidden;
            transition: max-height .5s ease;
        }
    }
</style>
